import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Mail, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { PushDevices } from "@/components/admin/PushDevices";

const ORDER = ["custom_plan", "dev_message", "capacity", "capacity_recovery", "robots", "queue_throttle", "billing", "fees", "paid_plans", "review_blocked", "atelier", "grace"];

const PaidRecent = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/notifications/paid").then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div className="mt-4 border-t border-white/5 pt-3" data-testid="notif-paid-recent">
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("notif.paidRecent")}</p>
      <ul className="mt-2 space-y-1">
        {items.map((n, i) => (
          <li key={n.at + i} className="break-words text-xs text-neutral-300" data-testid={`notif-paid-item-${i}`}>
            <span className="text-neutral-500">{new Date(n.at).toLocaleString()}</span> · {n.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Row = ({ k, v, onChange }) => {
  const { t } = useI18n();
  return (
    <li className="flex items-center gap-3 border-t border-white/5 py-2.5" data-testid={`notif-row-${k}`}>
      <span className="min-w-0 flex-1 break-words text-sm text-neutral-200">{t(`notif.cat.${k}`)}</span>
      <Switch checked={v.email} onCheckedChange={(x) => onChange(k, "email", x)} aria-label="email" data-testid={`notif-${k}-email`} />
      <Switch checked={v.push} onCheckedChange={(x) => onChange(k, "push", x)} aria-label="push" data-testid={`notif-${k}-push`} />
    </li>
  );
};

export const NotificationsCard = () => {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/notifications").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  const change = async (k, channel, value) => {
    const categories = { ...s.categories, [k]: { ...s.categories[k], [channel]: value } };
    setS({ ...s, categories });
    try {
      const { data } = await api.put("/admin/notifications", { categories });
      setS(data);
      toast.success(t("notif.saved"));
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  if (!s) return null;
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5 lg:col-span-2" data-testid="notifications-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><Bell className="h-4 w-4 text-[var(--gold)]" /> {t("notif.title")}</h2>
      <p className="mt-2 text-xs text-neutral-400">{t("notif.hint")}</p>
      {s.env === "preview" && <p className="mt-2 rounded-lg border border-amber-300/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-100" data-testid="notif-preview-badge">{t("notif.preview")}</p>}
      <div className="mt-3 flex justify-end gap-3 text-neutral-400">
        <span className="flex w-9 justify-center" title={t("notif.email")}><Mail className="h-4 w-4" aria-label={t("notif.email")} /></span>
        <span className="flex w-9 justify-center" title={t("notif.push")}><Smartphone className="h-4 w-4" aria-label={t("notif.push")} /></span>
      </div>
      <ul className="mt-1">{ORDER.map((k) => <Row key={k} k={k} v={s.categories[k]} onChange={change} />)}</ul>
      <PaidRecent />
      <PushDevices />
    </section>
  );
};
